import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2026-07-28 15:09:36 +0200 (Tue, 28 Jul 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 164661 $
//  Purpose   : dashboard part for tree/id and general dashboard panels
// =============================================================================
// ---Third-party imports
import React, { createElement } from 'react';
import { Table, Grid, List, Menu, Breadcrumb, Segment } from 'semantic-ui-react';
// ---Framework imports
import { Actions, Sel } from 'uix/registries';
import { DashboardPanels } from 'uix/registry';
import { funcPart, loadLazy } from 'uix/parts';
import log from 'uix/logging';
import { mapItems, iterItems, isNil, sorted, items, size, values, get, isFunction, dict, flatten, strip, filterItems } from 'ldash';
import { Link, HeaderSegment, DebugFeature, Dialog, XMenu } from 'uix/components';
import { usePersistentToggle } from 'uix/components/menu';
import { useSelector, useApi, useCallApi, usePersistentShallow, useState, useResettableState, useEffect, useRef, useGraphics, useRecordDataChanged, useMemo } from 'uix/hooks';
import { useTreeList, useColumnsForNode, useTreeFieldSchema, useTreeNodeFields } from 'uix/db/hooks';
import { TreeTable, LuceneTable } from 'uix/db/table';
import { PropertyTable as PropertyTableBase } from 'uix/db/proplist';
import { getColumnSet } from 'uix/db/colsets';
import { useWikiRendered } from 'uix/wiki/hooks';
import { WikiPane } from 'uix/wiki/render';
import { Graphics, VIcon } from 'uix/graphics';
import { MiniTitleInfo, PageTitleInfo } from 'uix/frontend/components';
import { AclFeature, UserInfo } from 'uix/auth/components';
import { checkAcl } from 'uix/auth/acl';
import { DashGrid, DashSegment } from 'uix/components/grid';
import { getStateDashboardPanels, getAllStateDashboardPanels } from 'uix/states/dashboard';
import { useVlmEnabledStates } from 'uix/states/hooks';
import { SinglePageViewer } from 'uix/viewer/components';
import { SingleFileUploader } from 'uix/upload/components';
import { TreeNodeLinksPanel } from 'uix/tree/components';
// ---Local Imports
import { WebcacheTable } from './webcache';
import { MailPanel } from './panels/mailPanel';
import S from './node_overview.css';
import { TreeParentHistory } from './treeParentHistory';
// ---Globals
const usePhysicalNode = ()=>{
    return useSelector(Sel.currentNodePhysical);
};
const INITIAL_OPTIONS = {
    showAll: false,
    showEmpty: false,
    viewraw: false
};
const usePersistentNodeOptions = ()=>usePersistentShallow('NodePropertiesOptions', INITIAL_OPTIONS);
const NodePropertiesMenu = ({})=>{
    let [opts, setOpts] = usePersistentNodeOptions();
    return /*#__PURE__*/ _jsx(AclFeature, {
        acl: "g:admin",
        children: /*#__PURE__*/ _jsxs(XMenu, {
            size: "small",
            padded: true,
            children: [
                /*#__PURE__*/ _jsx(Menu.Item, {
                    icon: "filter",
                    title: "Filter empty properties",
                    active: !opts.showEmpty,
                    onClick: ()=>setOpts({
                            showEmpty: !opts.showEmpty
                        })
                }),
                /*#__PURE__*/ _jsxs(DebugFeature, {
                    children: [
                        /*#__PURE__*/ _jsx(Menu.Item, {
                            icon: "filter",
                            title: "Filter properties",
                            active: !opts.showAll,
                            onClick: ()=>setOpts({
                                    showAll: !opts.showAll
                                })
                        }),
                        /*#__PURE__*/ _jsx(Menu.Item, {
                            icon: "bug",
                            title: "Filter field names and raw values",
                            active: opts.viewraw,
                            onClick: ()=>setOpts({
                                    viewraw: !opts.viewraw
                                })
                        })
                    ]
                })
            ]
        })
    });
};
const PropertyTable = ({ columnList, namespaceLabels = false, buildLabel = null })=>{
    let [opts] = usePersistentNodeOptions();
    return /*#__PURE__*/ _jsx(PropertyTableBase, {
        columnList: columnList,
        namespaceLabels: namespaceLabels,
        buildLabel: buildLabel,
        raw: opts.viewraw
    });
};
let useColumnData = ({ fields, ...params })=>{
    let nodeData = usePhysicalNode();
    let columnList = useColumnsForNode({
        fields,
        nodeData,
        ...params
    });
    return {
        nodeData,
        columnList
    };
};
const FilteredPropertyTable = ({ node, showEmpty, fields })=>{
    if (!fields) {
        fields = [
            'type',
            'caption',
            'otid',
            'release.value'
        ];
        if (node.delstate) {
            fields = [
                ...fields,
                'delstate'
            ];
        }
        // curated list - see UnfilteredPropertyTable for server-side all
        if (node.datatypeName == 'metadata') {
            if (node.odlink) {
                fields = [
                    'domain',
                    ...fields
                ];
            }
            fields = [
                ...fields,
                ...[
                    'license.value',
                    //~ 'metaparent',
                    'qs.value',
                    'wf.value',
                    'metadata.value',
                    'zipcapsule.value',
                    'host.*',
                    'oc.sourceident',
                    'urn.ident',
                    'oc.xmlorigin',
                    'oc.signature',
                    'oc.owner',
                    'oc.physicallocation',
                    'oc.pagecache',
                    'oc.releasedate',
                    'oc.ot_titlepage',
                    'oc.date',
                    'oc.enddate',
                    'oc.maxaccesscount',
                    'oc.textstate',
                    'accstats.downloads',
                    'accstats.visits',
                    'oc.comment',
                    'oc.bibcomment',
                    'oc.email',
                    'oc.oaiident'
                ]
            ];
        } else if (node.datatypeName == 'domain') {
            fields = [
                'od.name',
                'type',
                'odid',
                'otid',
                'od.shortname',
                'od.state'
            ];
        } else if (node.datatypeName == 'page') {
            fields = [
                ...fields,
                ...[
                    'op.paginationcaption',
                    'op.mimetype',
                    'op.bpp',
                    'op.imgwidth',
                    'op.imgheight',
                    'op.filesize',
                    'op.iccstate',
                    'oip.dpix',
                    'oip.software',
                    'oip.camera',
                    'oip.creator',
                    'op.archivecfg',
                    'op.state',
                    'oor.wordcount',
                    'oor.linecount',
                    'oor.charconf',
                    'oor.wer'
                ]
            ];
        } else if (node.datatypeName == 'name') {
            fields = [
                ...fields,
                ...[
                    'on.name',
                    'on.date',
                    'on.ident',
                    'on.localident',
                    'on.wdident'
                ]
            ];
        } else if (node.datatypeName == 'place') {
            fields = [
                ...fields,
                ...[
                    'opp.ident',
                    'opp.geoident',
                    'opp.wdident'
                ]
            ];
        } else if (node.datatypeName == 'subject') {
            fields = [
                ...fields,
                ...[
                    'oss.ident'
                ]
            ];
        } else if (node.datatypeName == 'glossary') {
            fields = [
                ...fields,
                ...[
                    'og.ident',
                    'og.enabled'
                ]
            ];
        } else if (node.type == 'webpage') {
            fields = [
                ...fields,
                ...[
                    'ow.ident',
                    'ow.mimetype'
                ]
            ];
        } else if (node.datatypeName == 'webresource') {
            fields = [
                ...fields,
                ...[
                    'webres.mimetype',
                    'webres.filesize',
                    //~ 'webres.filename',
                    'webres.modificationtimestamp'
                ]
            ];
        } else if (node.datatypeName == 'substructure') {
            fields = [
                ...fields,
                ...[
                    'structid.ident'
                ]
            ];
        } else if (node.datatypeName == 'topstructure') {
            fields = [
                ...fields,
                ...[
                    'structid.ident'
                ]
            ];
        }
    }
    let columnList = useColumnsForNode({
        fields,
        nodeData: node,
        showEmpty
    });
    return /*#__PURE__*/ _jsx(PropertyTable, {
        columnList: columnList
    });
};
const UnfilteredPropertyTable = ({ node, showEmpty })=>{
    let schema = useTreeFieldSchema();
    let fieldsForDatatype = schema[node.datatype];
    let fields = [
        'type',
        'otid',
        'caption',
        ...sorted(fieldsForDatatype || [])
    ];
    let columnList = useColumnsForNode({
        fields,
        nodeData: node,
        showEmpty
    });
    return /*#__PURE__*/ _jsx(PropertyTable, {
        columnList: columnList
    });
};
const NodeProperties = ({ i18n, node, fields })=>{
    let graphics = useGraphics([
        'types',
        'states'
    ]);
    let [opts, setOpts] = usePersistentNodeOptions();
    let table = opts.showAll ? /*#__PURE__*/ _jsx(UnfilteredPropertyTable, {
        node: node,
        showEmpty: opts.showEmpty
    }) : /*#__PURE__*/ _jsx(FilteredPropertyTable, {
        fields: fields,
        node: node,
        showEmpty: opts.showEmpty
    });
    return /*#__PURE__*/ _jsx(DashSegment, {
        header: i18n.span('props'),
        icon: "info",
        menu: !fields && /*#__PURE__*/ _jsx(NodePropertiesMenu, {}),
        children: table
    });
};
const NodeActivity = ({ i18n, node })=>{
    let fields = [
        'insertts',
        'release.changets',
        'qs.changets',
        'wf.changets',
        'zipcapsule.changets',
        'metadata.changets',
        'ar.changets',
        'license.changets',
        'srusync.changets',
        'gndsync.changets',
        'ow.updatets',
        'pdf.insertts',
        'pdfcutter.changets',
        'ocr.insertts',
        'op.imgdatetime',
        'urn.updatets',
        'doi.updatets',
        'od.configupdatets',
        'od.cssupdatets',
        'oc.oaits'
    ];
    let { columnList } = useColumnData({
        fields,
        showEmpty: false
    });
    columnList = sorted(columnList, (col)=>col.value);
    let title = 'Move Operations';
    let menu = /*#__PURE__*/ _jsx(XMenu, {
        padded: true,
        children: /*#__PURE__*/ _jsx(XMenu.DialogItem, {
            dlgHeader: title,
            title: title,
            icon: "wys_marginNote-left",
            content: "Moves",
            children: /*#__PURE__*/ _jsx(TreeParentHistory, {
                otid: node.otid
            })
        })
    });
    return /*#__PURE__*/ _jsx(DashSegment, {
        header: i18n.span('activity'),
        subheader: null,
        icon: "time",
        menu: menu,
        children: /*#__PURE__*/ _jsx(PropertyTable, {
            columnList: columnList,
            namespaceLabels: true
        })
    });
};
const NodeTranslations = ({ i18n })=>{
    let dconfig = useSelector(Sel.dconfig);
    let fields = [
        'otr.en',
        'otr.fr',
        'otr.it'
    ];
    let { columnList } = useColumnData({
        fields,
        showEmpty: false
    });
    if (dconfig.language.supported.length < 2) {
        return null;
    }
    columnList = sorted(columnList, (col)=>col.value);
    // this i18n data block is strange
    let buildLabel = (i18nKey)=>i18n.get(`lang_${i18nKey.split('.').slice(-1)[0]}`);
    return /*#__PURE__*/ _jsx(DashSegment, {
        header: i18n.get('Translations'),
        subheader: null,
        icon: "world",
        children: /*#__PURE__*/ _jsx(PropertyTable, {
            columnList: columnList,
            buildLabel: buildLabel
        })
    });
};
const NodeUserRelations = ({ i18n, node })=>{
    let { data, error } = useApi('tree/getNodeUsers', {
        otid: node.otid
    });
    let relation_map = {
        1: 'esa_order',
        2: 'user_created',
        3: 'user_list_member',
        4: 'deliverer_owner',
        5: 'workshop_instructor',
        6: 'workshop_participant_root',
        7: 'workshop participant_material'
    };
    let users = data?.users || [];
    if (users.length == 0) {
        return null;
    }
    let rows = users.map(({ puid, reltype })=>/*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsx(UserInfo, {
                    puid: puid
                }),
                ` (${relation_map[reltype] || reltype})`
            ]
        }, `${puid}_${reltype}`));
    return /*#__PURE__*/ _jsx(DashSegment, {
        header: i18n.get('user_tree_relation'),
        subheader: null,
        icon: "user",
        children: rows
    });
};
const NodeChildren = ({ node })=>{
    let graphics = useGraphics([
        'types'
    ]);
    let i18n = useSelector(Sel.i18n);
    let [descendants, setDescendants] = useState(false);
    let [filterable, setFilterable] = useState(false);
    let [limited, setLimited] = useState(true);
    // XXX: maybe use treenode-data directly?
    let { columnList, nodeData } = useColumnData({
        fields: [
            'haschildren'
        ]
    });
    let haschildren = columnList[0]?.value;
    let { treeList } = useTreeList('overviewChildren', {
        parentid: nodeData.otid,
        parentType: nodeData.datatype,
        descendants,
        limit: limited ? 10 : 100
    });
    if (!haschildren || !graphics.types) {
        return null;
    }
    let columns = [
        'tcaption',
        ...getColumnSet('reduced', node.type)
    ];
    let menu = /*#__PURE__*/ _jsxs(XMenu, {
        children: [
            /*#__PURE__*/ _jsx(XMenu.Toggle, {
                icon: "sitemap",
                value: descendants,
                set: setDescendants,
                title: "Descendants"
            }),
            /*#__PURE__*/ _jsx(XMenu.Toggle, {
                icon: "filter",
                value: filterable,
                set: setFilterable
            }),
            /*#__PURE__*/ _jsx(XMenu.Toggle, {
                title: "Limit to 10",
                icon: "compress",
                value: limited,
                set: setLimited
            })
        ]
    });
    return /*#__PURE__*/ _jsx(DashSegment, {
        header: i18n.get('list'),
        subheader: null,
        icon: "list",
        menu: menu,
        children: /*#__PURE__*/ _jsx(TreeTable, {
            defaultColumns: columns,
            options: {
                selectable: false,
                filterable
            },
            data: treeList,
            showPaginationTop: false
        })
    });
};
const WIKI_PREVIEW_MAX_HEIGHT = 520;
const NodeWikiPreview = ({ go, i18n, node })=>{
    let wikiParams = node.datatypeName == 'domain' ? {
        ident: 'home'
    } : {
        otid: node.otid
    };
    let wiki = useWikiRendered(wikiParams);
    let contentRef = useRef();
    let [expanded, setExpanded] = useState(false);
    let [overflows, setOverflows] = useState(false);
    let rendered = useMemo(()=>{
        let children = wiki?.rendered?.$children;
        let text = wiki?.rendered?.$text;
        if (!children && isNil(text)) return null;
        return {
            $children: children,
            $text: text
        };
    }, [
        wiki?.rendered
    ]);
    useEffect(()=>{
        if (!contentRef.current) return;
        let updateOverflow = ()=>{
            let el = contentRef.current;
            setOverflows(el.scrollHeight > WIKI_PREVIEW_MAX_HEIGHT + 1);
        };
        updateOverflow();
        if (typeof ResizeObserver === 'undefined') return;
        let observer = new ResizeObserver(updateOverflow);
        observer.observe(contentRef.current);
        return ()=>observer.disconnect();
    }, [
        rendered
    ]);
    if (rendered) {
        let editParams = wiki.otid ? {
            id: wiki.otid
        } : {
            query: {
                identifier: wiki.identifier
            }
        };
        let editWiki = ()=>go({
                classname: 'xwiki',
                tabname: 'edit',
                ...editParams
            });
        let menu = /*#__PURE__*/ _jsx(XMenu, {
            size: "small",
            children: /*#__PURE__*/ _jsx(XMenu.Item, {
                icon: "edit",
                onClick: editWiki
            })
        });
        // images should not overflow
        let style = {
            overflow: 'hidden',
            backgroundColor: wiki?.meta?.backgroundColor
        };
        let previewStyle = {
            maxHeight: expanded ? undefined : WIKI_PREVIEW_MAX_HEIGHT,
            '--node_wiki_preview_bgColor': wiki?.meta?.backgroundColor || 'var(--vl_div_bgColor)'
        };
        let previewClassName = `${S.wikiPreviewContent} ${expanded || !overflows ? S.wikiPreviewExpanded : ''}`;
        return /*#__PURE__*/ _jsxs(DashSegment, {
            header: i18n.get('Wiki'),
            icon: "edit",
            menu: menu,
            style: style,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    ref: contentRef,
                    className: previewClassName,
                    style: previewStyle,
                    children: /*#__PURE__*/ _jsx("div", {
                        style: {
                            overflow: 'visible'
                        },
                        children: /*#__PURE__*/ _jsx(WikiPane, {
                            rendered: rendered,
                            identifier: wiki.identifier,
                            otid: wiki.otid,
                            resolveWidgets: true
                        })
                    })
                }),
                overflows && /*#__PURE__*/ _jsx("button", {
                    type: "button",
                    className: S.wikiPreviewToggle,
                    onClick: ()=>setExpanded(!expanded),
                    children: expanded ? 'Weniger anzeigen' : 'Alles anzeigen'
                })
            ]
        });
    }
    return null;
};
const NodeLinks = ({ i18n, node })=>/*#__PURE__*/ _jsx(TreeNodeLinksPanel, {
        header: i18n.get('Links'),
        node: node
    });
const NodeTitle = ({ i18n, node })=>{
    let { otlink, otid } = node;
    return /*#__PURE__*/ _jsx(DashSegment, {
        header: i18n.get('title'),
        children: /*#__PURE__*/ _jsx(MiniTitleInfo, {
            className: S.nodeTitle,
            thumbWidth: 80,
            otid: otlink || otid
        })
    });
};
const NodeSubstructTitleinfo = ({ i18n, node })=>{
    return /*#__PURE__*/ _jsx(DashSegment, {
        header: i18n.get('title'),
        children: /*#__PURE__*/ _jsx(PageTitleInfo, {
            className: S.nodeTitle,
            thumbWidth: 80,
            node: node
        })
    });
};
const NodeWebcache = ({ i18n, node })=>{
    return /*#__PURE__*/ _jsx(DashSegment, {
        header: i18n.get('Webcache'),
        icon: "list",
        children: /*#__PURE__*/ _jsx(WebcacheTable, {
            compact: true,
            otid: node.otid
        })
    });
};
const NodeResources = ({ i18n, node })=>{
    let fields = [
        'pdf',
        'ocr',
        'nerres',
        'fpk_plain',
        'fpk_alto',
        'fpk_mads',
        'fpk_html',
        'tei',
        'html',
        'mhtml',
        'genres'
    ].map((n)=>`resource.${n}`);
    let { columnList } = useColumnData({
        fields,
        showEmpty: false
    });
    if (columnList.length == 0) {
        return null;
    }
    columnList = sorted(columnList, (col)=>col.value);
    return /*#__PURE__*/ _jsx(DashSegment, {
        header: i18n.span('resources'),
        subheader: null,
        children: /*#__PURE__*/ _jsx(PropertyTable, {
            columnList: columnList
        })
    });
};
const DomainReleaseHistory = ({ i18n })=>{
    return /*#__PURE__*/ _jsx(DashSegment, {
        header: i18n.span('tabHistory'),
        children: /*#__PURE__*/ _jsx(LuceneTable, {
            defaultColumns: [
                'dicon',
                'tcaption',
                'oc.releasedate',
                'release.pu_id'
            ],
            query: "releaseHistory",
            groupHeaders: false,
            showPaginationTop: false
        })
    });
};
const NodeImageViewer = ({ i18n })=>{
    let node = usePhysicalNode();
    let [areaWidth, toggleAreaWidth] = usePersistentToggle('areaWidth', {
        icon: 'arrows alternate horizontal',
        title: i18n.get('fitToWidth')
    });
    let options = {
        zoom: {
            mode: areaWidth ? 'areaWidth' : 'areaHeight'
        }
    };
    let menu = /*#__PURE__*/ _jsx(XMenu, {
        size: "small",
        padded: true,
        children: toggleAreaWidth
    });
    return /*#__PURE__*/ _jsx(DashSegment, {
        header: i18n.span('image'),
        subheader: null,
        menu: menu,
        children: /*#__PURE__*/ _jsx(SinglePageViewer, {
            vid: "dashimage",
            otid: node.otid,
            height: 400,
            options: options
        })
    });
};
const UploadDashpanel = ({ node, apiurl })=>{
    /* upload a file - generalize to other datatypes (currently only save/webresource)
   */ let { callApi, calling } = useCallApi('wiki/saveWebresource');
    let [uploadResult, setUploadResult] = useResettableState(null, [
        node.otid
    ]);
    let [progress, setProgress] = useState(null);
    let src = null;
    if (uploadResult?.uploadid) {
        src = apiurl.href(`/api/upload/get?id=${uploadResult.uploadid}`);
    }
    let menu = /*#__PURE__*/ _jsxs(XMenu, {
        size: "small",
        padded: true,
        children: [
            progress?.active && /*#__PURE__*/ _jsx(XMenu.Item, {
                icon: {
                    name: 'circle notch',
                    loading: true
                },
                content: `${progress.percentage.toFixed(1)}%`
            }),
            /*#__PURE__*/ _jsx(XMenu.Item, {
                icon: "save",
                content: uploadResult?.filename || '',
                disabled: !uploadResult || calling,
                onClick: ()=>callApi({
                        otid: node.otid,
                        uploadid: uploadResult.uploadid
                    })
            })
        ]
    });
    return /*#__PURE__*/ _jsx(DashSegment, {
        header: "Upload",
        menu: menu,
        children: /*#__PURE__*/ _jsxs(SingleFileUploader, {
            onUploadDone: setUploadResult,
            onUploadProgress: setProgress,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    children: "Drop Here"
                }),
                src && /*#__PURE__*/ _jsx("img", {
                    src: src,
                    width: "100%"
                })
            ]
        })
    });
};
const PageUploadDashpanel = ({ node, apiurl })=>{
    /* upload multiple pages
   */ return /*#__PURE__*/ _jsx(DashSegment, {
        header: "Upload Pages/Images",
        children: "XXX"
    });
};
const WebresourceDashpanel = ({ node, apiurl })=>{
    let domains = useSelector(Sel.domains);
    let dprefix = `/${domains.odid2name[node.odid]}`;
    let href = apiurl.href(`${dprefix}/download/webresource/${node.otid}`);
    let last_rec_change = useRecordDataChanged({
        otid: node.otid
    }).at(-1)?.evt_ts;
    let refresh_param = last_rec_change ? `?ts=${last_rec_change}` : '';
    let content = /*#__PURE__*/ _jsx("a", {
        href: href,
        children: href
    });
    if (node.type == 'webimage') {
        // some bg color for stuff with alpha channel (and white fonts)
        content = /*#__PURE__*/ _jsx("img", {
            style: {
                backgroundColor: '#CCC'
            },
            width: "100%",
            src: apiurl.href(`${dprefix}/download/webresource/${node.otid}${refresh_param}`)
        });
    } else if (node.type == 'other' && node.caption.endsWith('.mp4')) {
        content = /*#__PURE__*/ _jsx("video", {
            width: "100%",
            controls: true,
            children: /*#__PURE__*/ _jsx("source", {
                src: apiurl.href(`${dprefix}/download/webresource/${node.otid}`),
                type: "video/mp4"
            })
        });
    }
    return /*#__PURE__*/ _jsx(DashSegment, {
        header: "Resource",
        menu: null,
        children: content
    });
};
export const NodeOverview = ({ partcfg, request })=>{
    /* build DashGrid from panels for tree/id
   */ let node = usePhysicalNode();
    let isStateMode = node?.datatypeName == 'domain' && String(request.id).includes('~byState');
    let vlmEnabledStates = useVlmEnabledStates(isStateMode);
    //~ let [opts, setOpts] = usePersistentNodeOptions()
    //~ return null
    if (!node) {
        return null;
    }
    let panels = [];
    let createCustomCfg = (panels)=>mapItems(panels, (name, cfg)=>({
                ...DashboardPanels[name],
                ...cfg
            }));
    if (partcfg.panels) {
        cfgs = createCustomCfg(partcfg.panels);
    } else if (isStateMode) {
        let s = String(request.id).split('~byState').slice(-1)[0];
        let [statusType, statusName] = strip(s, '~').split('~');
        if (statusType) {
            panels = getStateDashboardPanels({
                statusType,
                statusName
            });
        } else {
            panels = getAllStateDashboardPanels(vlmEnabledStates);
        }
    } else {
        // auto filtered by datatype (cfg.datatypes)
        // a subset of DashboardPanels
        panels = [
            'NodeTitle',
            'NodeSubstructTitleinfo',
            'NodeProperties',
            //~ 'NodeImageViewer',
            'NodeResources',
            'NodeStats',
            'NodeOverviewPanel',
            'NodeGndoDetails',
            'NodeChildren',
            'NodeUserRelations',
            'NodeWebcache',
            'NodeLinks',
            'MailPanel',
            'NodeActivity',
            'DomainReleaseHistory',
            //~ 'NodeTranslations',
            'NodeWikiPreview',
            'UploadDashpanel',
            //~ 'PageUploadDashpanel',
            'WebresourceDashpanel',
            // from other modules
            'PageboxDashpanel',
            'NodeTimeline',
            'NodeGndoInfo',
            'NodeRestrictions'
        ];
    }
    return /*#__PURE__*/ _jsx(DashGrid, {
        node: node,
        panels: panels
    });
};
export const panels = {
    NodeTitle,
    NodeSubstructTitleinfo,
    NodeProperties,
    NodeResources,
    NodeImageViewer,
    NodeTranslations,
    NodeChildren,
    NodeUserRelations,
    NodeWebcache,
    NodeLinks,
    MailPanel,
    NodeActivity,
    DomainReleaseHistory,
    NodeWikiPreview,
    UploadDashpanel,
    PageUploadDashpanel,
    WebresourceDashpanel
};
